/**
 * The global ⌘K / Ctrl+K binding.
 *
 * Mounted once, near useTextZoom in App — which is the only other window-level key
 * handler in the app, and does not overlap (it owns ⌘= / ⌘- / ⌘0).
 *
 * ⌘K is intercepted unconditionally, including from inside inputs. That is the whole
 * point of a palette shortcut: it has to work while you are typing somewhere else, or you
 * have to leave the field first to use it. Browsers bind ⌘K to the address bar, so
 * preventDefault is required, not optional.
 */
import { useEffect } from 'react'

import { useSearchStore } from 'search'

export const useSearchHotkey = () => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') return
      if (!(e.metaKey || e.ctrlKey)) return
      // Alt+⌘K and friends belong to the OS or the browser; only the bare combo is ours.
      if (e.altKey || e.shiftKey) return

      e.preventDefault()
      const { open, openSearch, closeSearch } = useSearchStore.getState()
      // Toggle rather than open. Pressing the same combo again to dismiss is the
      // behaviour every palette has, and it means the shortcut never traps you.
      if (open) closeSearch()
      else openSearch()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
